import Enumerable from "linq";
import Moment from "moment";

import { DomainRegistry } from "../../../domain";
import {
  Document,
  DocumentRegistrationResult,
  IDocumentRepository,
  DocumentSearchParameter,
  DocumentSummary,
  DocumentSummaryHistory,
  IDocumentImageSource,
  ImageSourceKinds,
  ReferenceFileInformation,
  UnmanagedDocument,
  CurrentEditors,
  DocumentObjectsInformation,
  DocumentRequestErrorCodes,
  INewDocumentRequest,
  DocumentSearchPresetCondition,
  IDocumentPublished,
  DocumentListPresetSortCondition,
  SingleEntryCheckInformation,
  DocumentSummarySearchOption,
  DocumentSearchResult,
} from "../../../domain/document";

import {
  IItem,
} from "../../../domain/document/items";

import {
  Department,
} from "../../../domain/department";

import { IWizard } from "../../../domain/format";
import {
  DocumentPreviewSource,
  DocumentPrintSource,
  ObjectTranslator,
  ImageSource,
  CsvSource,
  DocumentExchangePaperPrintSource,
} from ".";
import {
  IDocumentOperationMessageBean,
  IDocumentStatusBean,
  IDocumentSummaryBean,
  IDocumentSummaryHistoryBean,
  INewDocumentRequestBean,
  IUnmanagedDocumentBean,
  INewDocumentAlternativeRequestBean,
  ISingleEntryCheckInformationBean,
  ICurrentEditorBean,
  IDocumentSearchParameterBean,
  IDocumentSearchResultBean,
} from "./beans";
import { PrintPdfSource } from "./PrintPdfSource";
import { IPrintPdfSource } from "../../../domain/drawing/IPrintPdfSource";
import { IContentSource } from "../../../domain/drawing";
import { HisInformation } from "../../../domain/his";
import { PatientProfileInformation } from "../../../domain/patient";
import { DesignatedDoctorInformation } from "../../../domain/user";
import { AuthenticatedService, CachedDataAccessor } from "../..";
import { ReferenceFileSource } from "./ReferenceFileSource";
import { DocumentCacheSource } from "./DocumentCacheSource";
import { IDocumentRegistrationResultBean } from "./beans/IDocumentRegistrationResultBean";
import store from "../../../../src/store";
import { IConfiguration, NewObjectKeys, NewSignObjectKeys } from "../../../domain/configuration";
import { WizardTranslator } from "../../format";
import { ObjectUtility } from "../../../utilities";
import { LoggerFactory } from "../../../utilities/logger";

export class DocumentRepository extends AuthenticatedService implements IDocumentRepository {
  constructor(getToken: () => string) {
    super(getToken);
    this.translator = new ObjectTranslator(getToken);
  }

  private static _cache = {
    fontFamilyNames: new CachedDataAccessor<DocumentRepository, string[]>(async i => (await i.get<{ folders: string[] }>("api/document/font-names/get")).data.folders),
  };

  private readonly translator: ObjectTranslator;

  async getNewDocument(patientNo: string, formatId: number, institutionNo: string, regionalInstitutionNo: string, hisInfoGetterParameter: string, systemKey: string, linkKey: string): Promise<Document> {
    const configuration = store.getters.configuration as IConfiguration;
    if (configuration && configuration.behavior.document.editor.singleEntry.isEnable) {
      // 重複記載防止のチェックするときにプリフェッチで記載トークンが登録されていると支障があるため、ここでガードしている
      // ここを通るのはプリフェッチから呼ばれたときのみで、実際の文書編集画面の処理では取得前に分岐させるのでここに到達しない
      // また、重複チェックのAPIを叩いた結果はプリフェッチされるので無駄にはならない見込み
      const singleEntryInfo = await this.getSingleEntryCheckInformation(patientNo, formatId);
      if (singleEntryInfo.registeredDocumentSummary || singleEntryInfo.editInformation) {
        LoggerFactory.getLogger().warn("重複記載防止のため新規文書取得を中止します。");
        console.debug(singleEntryInfo);
        return null;
      }
    }
    
    const results = await Promise.all([
      !systemKey && !linkKey
        ? this.get<any>(
            "/api/format/" + formatId,
          {
            params: {
              patientcode: patientNo,
              shouldCreateWriteToken: true,
              shouldCreateCreateToken: true,
            }
          })
        : this.get<any>(
          "/api/collaboration/link/new",
          {
            params: {
              formatId: formatId,
              patientcode: patientNo,
              systemKey: systemKey,
              linkKey: linkKey,
            }
          }),
      this.getPatientProfile(patientNo),
      this.getAllDesignatedDoctors(),
      this.getHisInformation(patientNo, institutionNo, regionalInstitutionNo, hisInfoGetterParameter, systemKey, linkKey),
    ]);
    const result = this.toDocument(results[0].data);
    result.patientProfile = results[1];
    result.designatedDoctorInformations = results[2];
    result.hisInformation = results[3];
    return result;
  }

  async getNewEssentialDocument(patientNo: string, formatId: number): Promise<Document> {
    const result = await this.get(
      "/api/format/" + formatId,
      {
        params: {
          patientcode: patientNo,
        }
      });
    return this.toDocument(result.data);
  }

  private toDocument(data: any): Document {
    return this.translator.toDocument(
      () => this.token,
      data);
  }

  private async getPatientProfile(patientNo: string): Promise<PatientProfileInformation> {
    return this.fetch(
      DomainRegistry.patient.getPatientProfileRepository(),
      r => r.getPatientProfileInformation(patientNo));
  }

  private async getAllDesignatedDoctors(): Promise<DesignatedDoctorInformation[]> {
    const configuration = store.getters.configuration as IConfiguration;
    if (!configuration.behavior.document.editor.designatedDoctorNumber.isEnable) {
      return [];
    }
    return this.fetch(
      DomainRegistry.user.getDesignatedDoctorRepository(),
      r => r.getAllEntities());
  }

  private async getHisInformation(patientNo: string, institutionNo: string, regionalInstitutionNo: string, hisInfoGetterParameter: string, systemKey: string, linkKey: string): Promise<HisInformation> {
    return this.fetch(
      DomainRegistry.his.getHisInformationRepository(),
        r => r.getHisInformation(patientNo, institutionNo, regionalInstitutionNo, hisInfoGetterParameter, systemKey, linkKey));
  }

  private async getHisInformationWithDocumentRelatedValues(patientNo: string, documentId: number): Promise<HisInformation> {
    const linkedDocumentRepository = DomainRegistry.collaboration.getLinkedDocumentRepository();
    const parameter = await linkedDocumentRepository.getValueReferenceParameter(documentId);
    return this.getHisInformation(
      patientNo,
      parameter ? parameter.institutionNo : "",
      parameter ? parameter.regionalInstitutionNo : "",
      parameter ? parameter.hisInfoGetterParameter : "",
      parameter ? parameter.systemKey : "",
      parameter ? parameter.linkKey : "");
  }

  async getExtraDocument(patientNo: string, formatId: number): Promise<Document> {
    return this.getNewDocument(patientNo, formatId, null, null, null, null, null);
  }

  async getDocument(patientNo: string, documentId: number, editToken: string, writeToken: string, ignoreFixKey: string): Promise<Document> {
    const results = await Promise.all([
      this.get<any>(
        "/api/document/" + documentId + "/edit",
        {
          params: {
            patientcode: patientNo,
            editToken: editToken,
            writeToken: writeToken,
          }
        }),
      this.getPatientProfile(patientNo),
      this.getAllDesignatedDoctors(),
      this.getHisInformationWithDocumentRelatedValues(patientNo, documentId),
      this.getUnmanagedDocument(documentId),
    ]);
    const result = this.toDocument(results[0].data);
    result.patientProfile = results[1];
    result.designatedDoctorInformations = results[2];
    result.hisInformation = results[3];
    result.unmanagedInformation = results[4];

    // Fixを無視した挙動とするべきかどうかは本来であればAPI通信した先で判定したい
    if (ignoreFixKey) {
      result.attributes.isFixed = false;
      result.attributes.ignoreFixKey = ignoreFixKey;
    }
    return result;
  }

  async getDocumentForQuotation(patientNo: string, documentId: number): Promise<Document> {
    // 引用元文書については患者プロフィール/指定医情報/HIS情報は含めない.(使わないので.)
    const results = await Promise.all([
      this.get<any>(
        "/api/document/" + documentId + "/quote",
        {
          params: {
            patientcode: patientNo,
          }
        }),
        this.getUnmanagedDocument(documentId),
    ]);
    const result = this.toDocument(results[0].data);
    result.unmanagedInformation = results[1];
    return result;
  }

  async getDocumentForLatestQuotation(patientNo: string, formatId: number): Promise<Document> {
    // 引用元文書については患者プロフィール/指定医情報/HIS情報は含めない.(使わないので.)
    const result = await this.get<any>(
      "/api/document/latestQuote/" + formatId,
      {
        params: {
          patientcode: patientNo,
        }
      });
    if (!result.data) { return null; }
    const document = this.toDocument(result.data);
    document.unmanagedInformation = await this.getUnmanagedDocument(document.documentId);
    return document;
  }

  async getNewCopiedDocument(patientNo: string, documentId: number): Promise<Document> {
    const results = await Promise.all([
      this.get<any>(
        "/api/document/" + documentId + "/copy",
        {
          params: {
            patientcode: patientNo,
          }
        }),
      this.getPatientProfile(patientNo),
      this.getAllDesignatedDoctors(),
      this.getHisInformation(patientNo, "", "", "", "", ""),
    ]);
    const result = this.toDocument(results[0].data);
    result.patientProfile = results[1];
    result.designatedDoctorInformations = results[2];
    result.hisInformation = results[3];
    return result;
  }

  async cancelEditDocument(editToken: string): Promise<void> {
    if (!editToken) { return; }
    
    const url = "/api/document/cancel-edit";
    await this.post(url, {
      token: this.token,
      editToken: editToken,
    });
  }

  async deleteDocumentEditTokens(editTokens: string[]): Promise<void> {
    if (!editTokens || editTokens.length === 0) { return; }
    const url = "/api/document/delete-document-edit-tokens";
    await this.post(url, editTokens);
  }

  async extendEditTokenLifeSpan(editToken: string): Promise<void> {
    if (!editToken) { return; }

    const url = "/api/document/extend-edit-token-lifespan";
    await this.post(url, {
      token: this.token,
      editToken: editToken,
    });
  }

  async getCurrentEditTokens(documentId: number): Promise<CurrentEditors[]> {
    const url = "/api/document/" + documentId + "/current-edit-tokens";

    const result = await this.get(url);
    console.debug(result.data);
    return this.translator.toCurrentEditors(result.data);
  }

  async registerDocument(document: Document): Promise<DocumentRegistrationResult> {
    const url = "/api/document/save";
    const documentBean = this.translator.toSaveDocumentBean(document);
    console.debug(documentBean);
    const result = await this.post(url, documentBean);
    console.debug(result.data);
    return this.toDocumentRegistrationResult(result.data);
  }

  async registerChainedDocument(document: Document): Promise<DocumentRegistrationResult> {
    const url = "/api/document/save-chained-documents";
    const documentBean = this.translator.toSaveDocumentBean(document);
    console.debug(documentBean);
    const result = await this.post(url, documentBean);
    console.debug(result.data);
    return this.toDocumentRegistrationResult(result.data);
  }

  private toDocumentRegistrationResult(bean: IDocumentRegistrationResultBean): DocumentRegistrationResult {
    return new DocumentRegistrationResult(
      bean.isOk,
      bean.isOk
        ? {
          id: bean.documentId,
          archiveId: bean.archiveId,
          isFirstNormalRegister: bean.isFirstNormalRegister,
        }
        : {
          isDeleted: bean.items.isDeleted,
          isFixed: bean.items.isFixed,
          isRegisterableRegisteredDocumentAsTemporary: bean.items.isRegisterableRegisteredDocumentAsTemporary,
          edition: bean.items.edition,
          updated: {
            userName: bean.items.updateUserName,
            terminal: bean.items.updateTerminal,
            dateTime: bean.items.updateDateTime ? this.yyyyMmDdHhMmSsToDate(bean.items.updateDateTime) : null,
          },
        }
    );
  }

  async searchDocuments(param: DocumentSearchParameter): Promise<DocumentSearchResult> {
    const url = "/api/documents/search";
    const result = await this.post(url, this.toSearchQueryParameters(param));

    console.debug(result.data);
    return this.toDocumentSearchResults(result.data);
  }

  private toDocumentSearchResults(bean: IDocumentSearchResultBean): DocumentSearchResult {
    if (!bean) { throw new Error("文書検索に失敗しました。"); }

    const result = new DocumentSearchResult();
    result.summaries = this.toDocumentSummaries(bean.summaries);
    result.isExceededMaxRecordCountLimit = bean.isExceededMaxRecordCountLimit;
    return result;
  }

  private toSearchQueryParameters(param: DocumentSearchParameter): IDocumentSearchParameterBean {
    return {
      patientId: param.patientId,
      startDate: param.fromDate,
      endDate: param.toDate,
      departmentGroupId: param.departmentGroupId,
      departmentIds: param.departmentIds,
      departmentId: param.departmentId,
      containsDeleted: param.containsDeleted,
      containsTemporary: param.containsTemporary,
      containsRegistered: param.containsRegistered,
      documentKindIds: param.documentKindIds,
      loginUserId: param.loginUserId,
      contentDateSearch: param.contentDateSearch,
      loginUserOnly: param.loginUserOnly,
      formatName: param.formatName,
      formatIds: param.formatIds,
      manageNumberText: param.manageNumberText,
      containsVirtualDocuments: param.containsVirtualDocuments,
      updateUserCodes: param.updateUserCodes,
    };
  }

  toDocumentSummaries(beans: IDocumentSummaryBean[]): DocumentSummary[] {
    const results: DocumentSummary[] = [];
    for (const bean of beans) {
      results.push(this.toDocumentSummary(bean));
    }
    results.sort((a, b) => {
      return b.createdDateTime.valueOf() - a.createdDateTime.valueOf();
    });
    return results;
  }

  toDocumentSummary(bean: IDocumentSummaryBean): DocumentSummary {
    return new DocumentSummary(
      bean.documentId,
      bean.patientId,
      bean.edition,
      new DocumentPreviewSource(() => this.token, bean.documentId, bean.edition, bean.patientId),
      new DocumentPrintSource(() => this.token, bean.documentId, bean.edition, bean.patientId),
      bean.isPrintable,
      bean.formatName,
      bean.formatId,
      bean.patientName,
      new Department(
        bean.departmentCode,
        bean.departmentName,
        ""
      ),
      this.yyyyMmDdHhMmSsToDate(bean.createDateTime),
      bean.createOperatorName,
      bean.createTerminal,
      !!bean.isTemporary,
      bean.note ? bean.note : "",
      bean.readable,
      bean.deletable,
      bean.writable,
      bean.showReadWarning,
      bean.showLoginDialog,
      bean.useAuditLogWrite,
      this.yyyyMmDdHhMmSsToDate(bean.updateDateTime),
      bean.updateOperatorName,
      this.yyyyMmDdHhMmSsToDate(bean.contentDateTime),
      bean.reportTypeId,
      bean.reportType,
      bean.published,
      bean.publishedDateTime ? this.yyyyMmDdHhMmSsToDate(bean.publishedDateTime) : null,
      bean.publishedCount,
      bean.formatOriginalId,
      bean.isDeleted,
      this.yyyyMmDdHhMmSsToDate(bean.firstDocumentDateTime),
      bean.firstDocumentOperatorName,
      bean.documentType,
      bean.isFixed,
      bean.orderUserCode ? bean.orderUserCode : "",
      bean.orderUserName ? bean.orderUserName : "",
      bean.attendingUserCode ? bean.attendingUserCode : "",
      bean.attendingUserName ? bean.attendingUserName : "",
      bean.defaultCopies,
      bean.companyName,
      bean.isQuotable,
      bean.alternativeFormatOriginalId,
      bean.isUnmanaged,
      bean.isVirtual,
    );
  }

  yyyyMmDdHhMmSsToDate(source: string): Date {
    return Moment(source.slice(0, 8) + "T" + source.slice(8)).toDate();
  }

  async searchDocumentsAndCreateCsv(param: DocumentSearchParameter): Promise<IContentSource> {
    const url = "/api/documents/ext/csv-create-progress";
    const query = {
      params: this.toSearchQueryParameters(param)
    };
    const result = await this.get(url, query);
    console.debug(result.data);
    return new CsvSource(() => this.token, result.data);
  }

  async uploadDocumentImage(imageText: string): Promise<string> {
    const sendData = { image: imageText };
    const url = "/api/document/temporary/image-save";
    const result = await this.post(url, sendData);
    console.debug(result.data);
    return result.data as string;
  }

  async registerClaioImage(patientCode: string, claioImageId: string, isVisibleLayer: boolean): Promise<string> {
    if (!patientCode) { throw new Error("patientCode is empty!!!"); }
    const sendData = {
      token: this.token,  // postデータに自動的に入れるのは難しいので.
      patientCode,
      claioImageId,
      isVisibleLayer,
    };
    const url = "/api/document/temporary/image-save-from-claio";
    console.debug(sendData);
    const result = await this.post(url, sendData);
    console.debug(result.data);
    return result.data as string;
  }

  async copyImageFromSavedDocument(documentId: number, imageKey: string): Promise<string> {
    if (!documentId) { throw new Error("documentId is empty!!!"); }
    if (!imageKey) { throw new Error("imageKey is empty!!!"); }
    const url = "/api/document/" + documentId + "/image/" + imageKey + "/quote";
    const result = await this.get(url);
    console.debug(result.data);
    return result.data as string;
  }

  async createImageSource(imageText: string): Promise<IDocumentImageSource> {
    const imageGuid = await this.uploadDocumentImage(imageText);
    return new ImageSource(() => this.token, null, null, ImageSourceKinds.Cache, imageGuid);
  }

  async createImageSourceFromCachedGuid(guid: string): Promise<IDocumentImageSource> {
    return new ImageSource(() => this.token, null, null, ImageSourceKinds.Cache, guid);
  }

  async createEmptyImageSource(): Promise<IDocumentImageSource> {
    return new ImageSource(() => this.token, null, null, ImageSourceKinds.None, null);
  }

  async createImageSourceFromClaioImage(patientCode: string, claioImageId: string, isVisibleLayer: boolean, isMovie: boolean, isSecret: boolean, departmentId: string): Promise<IDocumentImageSource> {
    const imageGuid = await this.registerClaioImage(patientCode, claioImageId, isVisibleLayer);
    return new ImageSource(() => this.token, null, null, ImageSourceKinds.Cache, imageGuid);
  }

  async createImageSourceFromClaioSchema(templateId: number, groupId: number, itemId: number): Promise<IDocumentImageSource> {
    const imageGuid = await this.registerClaioSchemaImage(templateId, groupId, itemId);
    return new ImageSource(() => this.token, null, null, ImageSourceKinds.Cache, imageGuid);
  }

  async createImageSourceFromExternalImage(patientCode: string, externalImageId: string): Promise<IDocumentImageSource> {
    const imageGuid = await this.registerExternalImage(patientCode, externalImageId);
    return new ImageSource(() => this.token, null, null, ImageSourceKinds.Cache, imageGuid);
  }

  private async registerClaioSchemaImage(templateId: number, groupId: number, itemId: number): Promise<string> {
    const sendData = {
      token: this.token,
      templateId,
      groupId,
      itemId,
    };
    const url = "/api/document/temporary/image-save-from-claio-template";
    console.debug(sendData);
    const result = await this.post(url, sendData);
    console.debug(result.data);
    return result.data as string;
  }

  private async registerExternalImage(patientCode: string, externalImageId: string): Promise<string> {
    const sendData = {
      token: this.token,
      patientCode,
      externalImageId,
    };
    const url = "/api/document/temporary/image-save-from-external-image";
    console.debug(sendData);
    const result = await this.post(url, sendData);
    console.debug(result.data);
    return result.data as string;
  }

  async createPdf(document: Document): Promise<IPrintPdfSource> {
    const result = await this.post(
      "/api/document/pdf-print",
      this.translator.toSaveDocumentBean(document),
      {
        responseType: "blob",
      });
    const blob = new Blob([result.data], { type: "application/pdf" });
    const pdfSource = new PrintPdfSource(blob); // TODO: IEはBlob URO Schemeが使用できない。どう対応する？
    return pdfSource;
  }

  async getReferenceFile(fileId: number): Promise<IContentSource> {
    const source = new ReferenceFileSource(() => this.token, fileId);
    return source;
  }

  async createPreviewSource(document: Document): Promise<IContentSource> {
    return this.createPdfSource("/api/document/create-pdf-preview-cache", document);
  }

  async createPrintSource(document: Document, withoutWatermark: boolean): Promise<IContentSource> {
    return this.createPdfSource("/api/document/create-pdf-print-cache", document, withoutWatermark);
  }

  async createPrintPreviewSource(document: Document): Promise<IContentSource> {
    return this.createPdfSource("/api/document/create-pdf-print-preview-cache", document);
  }

  async createOriginalSource(document: Document): Promise<IContentSource> {
    return this.createPdfSource("/api/document/create-pdf-original-cache", document);
  }

  async createPreviewLikeEditingSource(documentId: number, patientCode: string, edition: number): Promise<IContentSource> {
    const configuration = store.getters.configuration as IConfiguration;
    const sendData = {
      documentId: documentId,
      patientCode: patientCode,
      edition: edition,
      objectStyle: configuration.style.document.editor.objects,
    };
    const result = await this.post("/api/document/create-pdf-preview-like-editing-cache", sendData);
    console.debug(result.data);
    return new DocumentCacheSource(() => this.token, result.data, false);
  }

  createDocumentExchangePaperPrintSource(patientCode: string, documentId: number, edition: number): IContentSource {
    return new DocumentExchangePaperPrintSource(() => this.token, patientCode, documentId, edition);
  }

  private async createPdfSource(url: string, document: Document, withoutWatermark: boolean = false): Promise<IContentSource> {
    const documentBean = this.translator.toSaveDocumentBean(document);
    const result = await this.post(url, documentBean);
    console.debug(result.data);
    return new DocumentCacheSource(() => this.token, result.data, withoutWatermark);
  }

  async registerDocumentNote(documentId: number, patientCode: string, note: string): Promise<boolean> {
    const url = "/api/document/" + documentId + "/note";
    const sendData = {
      token: this.token,
      patientCode,
      note,
    };
    await this.post(url, sendData);
    return true;
  }

  async getArchiveDocuments(documentId: number): Promise<DocumentSummaryHistory[]> {
    const url = "/api/document/" + documentId + "/archives";
    const result = await this.get(url);
    console.debug(result.data);
    return this.toDocumentSummaryHistories(documentId, result.data);
  }

  toDocumentSummaryHistories(documentId: number, beans: IDocumentSummaryHistoryBean[]): DocumentSummaryHistory[] {
    const maxEdition = (beans && (0 < beans.length))
      ? Enumerable.from(beans).max(b => b.edition)
      : 0;
    return Enumerable.from(beans)
      .select(b => new DocumentSummaryHistory(
        documentId,
        b.patientId,
        b.edition,
        new DocumentPreviewSource(() => this.token, documentId, b.edition, b.patientId),
        new DocumentPrintSource(() => this.token, documentId, b.edition, b.patientId),
        b.isPrintable,
        b.archiveId,
        b.departmentName,
        this.yyyyMmDdHhMmSsToDate(b.createDateTime),
        b.createOperatorName,
        b.createOperatorProfessionName,
        this.yyyyMmDdHhMmSsToDate(b.updateDateTime),
        b.updateOperatorName,
        b.published,
        b.isTemporary,
        b.edition === maxEdition,
        b.isDeleted,
        b.isFixed,
        b.defaultCopies,
        b.formatName,
        b.isQuotable,
        b.alternativeFormatOriginalId,
        b.isUnmanaged,
        b.note,
        b.writable,
        b.isVirtual,
        b.orderUserName,
        b.attendingUserName,
      ))
      .toArray();
  }

  async registerAuditLog(summary: DocumentSummary): Promise<void> {
    const url = "/api/documents/security-alert/save?token=" + this.token;
    const parameter = {
      documentId: summary.id,
      patientId: summary.patientNo,
      patientName: summary.patientName,
      formatId: summary.formatId,
      formatName: summary.documentName,
    };
    await this.post(url, parameter);
  }

  async deleteDocument(documentId: number, patientCode: string, orderUserCode: string, attendingUserCode: string): Promise<void> {
    const url = "/api/document/" + documentId + "/delete";
    const query = {
      params: {
        patientCode: patientCode,
        orderUserCode: orderUserCode,
        attendingUserCode: attendingUserCode,
      }
    };
    await this.get(url, query);
  }

  async getDocumentSummary(patientNo: string, documentId: number): Promise<DocumentSummary> {
    return this.getDocumentSummaryWithOption(patientNo, documentId, null);
  }

  async getDocumentSummaryWithOption(patientNo: string, documentId: number, option: DocumentSummarySearchOption): Promise<DocumentSummary> {
    const shouldIgnoreWorkflowDocumentPrintableMinimumStatus = !!option
      ? option.shouldIgnoreWorkflowDocumentPrintableMinimumStatus
      : false;
    const url = "/api/document/" + documentId + "/summary";
    const query = {
      params: {
        patientcode: patientNo,
        shouldIgnoreWorkflowDocumentPrintableMinimumStatus: shouldIgnoreWorkflowDocumentPrintableMinimumStatus,
      }
    };
    const result = await this.get(url, query);
    return this.toDocumentSummary(result.data);
  }

  async getDocumentReferencefiles(documentId: number, archiveId?: number): Promise<ReferenceFileInformation[]> {
    const url = "/api/document/" + documentId + "/referencefiles";
    const query = {
      params: {
        archiveId,
      }
    };
    const result = await this.get(url, query);
    return this.translator.toReferenceFiles(result.data);
  }

  async isEditingDocument(patientCode: string, documentId: number): Promise<boolean> {
    const url = "/api/document/" + documentId + "/status";
    const query = {
      params: {
        patientCode,
      }
    };
    const result = await this.get(url, query);
    const status = result.data as IDocumentStatusBean;
    return !!status.isEditing;
  }

  async getNewDocumentRequest(requestId: number): Promise<INewDocumentRequest> {
    // TODO: 本対応.
    const result = await this.get(
      "/api/document/factory/get-request",
      {
        params: {
          requestId,
        }
      }
    );
    return result && result.data && result.data.url
      ? this.translator.toNewDocumentAlternativeRequest(result.data as INewDocumentAlternativeRequestBean)
      : this.translator.toNewDocumentRequest(result.data as INewDocumentRequestBean);
  }

  async registerRequestedNewDocument(requestId: number, document: Document): Promise<void> {
    // TODO: 本対応.
    const url = "/api/document/factory/save";
    const documentBean = this.translator.toSaveDocumentBean(document);
    const query = {
      params: {
        requestId,
      }
    };
    await this.post(url, documentBean, query);
  }

  async registerFailureInformationToNewDocumentRequest(requestId: number, errorCode: DocumentRequestErrorCodes, message: string): Promise<void> {
    const url = "/api/document/factory/failure";
    const data = {
      requestId,
      token: this.token,
      errorCode,
      message,
    };
    await this.post(url, data);
  }

  async registerSuccessInformationToNewDocumentRequest(requestId: number, documentId: number, archiveId: number): Promise<void> {
    const url = "/api/document/factory/success";
    const data = {
      requestId,
      token: this.token,
      documentId,
      archiveId,
    };
    await this.post(url, data);
  }

  async deleteDocumentCreateToken(createToken: string): Promise<void> {
    if (!createToken) { return; }

    const url = "/api/document/" + createToken + "/document-create-token-delete";
    const query = {
      params: {
        token: this.token,
      }
    };
    await this.post(url, query);
  }

  async extendDocumentCreateTokenLifeSpan(createToken: string): Promise<void> {
    if (!createToken) { return; }

    const url = "/api/document/" + createToken + "/document-create-token-extend";
    const query = {
      params: {
        token: this.token,
      }
    };
    await this.post(url, query);
  }

  async getWizard(formatId: number, wizardKey: string): Promise<IWizard> {
    const result = await this.get(
      "/api/format/wizard/" + formatId,
      {
        params: {
          wizardKey: wizardKey
        }
      });
    if (!result.data) {
      return null;
    }
    const translator = new WizardTranslator();
    return translator.fromBean(result.data);
  }

  async getUnmanagedDocument(documentId: number): Promise<UnmanagedDocument> {
    const url = "/api/document/" + documentId + "/unmanaged-document";
    const result = await this.get(url);
    return this.toUnmanagedDocument(result.data);
  }

  private toUnmanagedDocument(bean: IUnmanagedDocumentBean): UnmanagedDocument {
    if (!bean) { return null; }
    return new UnmanagedDocument(
      bean.isQuotable,
      bean.alternativeFormatId,
    );
  }

  async getFontFamilyNames(): Promise<string[]> {
    return DocumentRepository._cache.fontFamilyNames.getData(this);
  }

  async createNewObjects(key: NewObjectKeys): Promise<IItem> {
    const result = this.translator.toObjectItem(this.getNewObjectBean(key), null, null, null, null);
    return result;
  }

  private getNewObjectBean(key: NewObjectKeys): any {
    const configuration = store.getters.configuration as IConfiguration;
    return ObjectUtility.copyDeeply(configuration.behavior.document.editor.newObjects[key].source);
  }

  async createNewSignObjects(key: NewSignObjectKeys): Promise<IItem> {
    const result = this.translator.toObjectItem(this.getNewSignObjectBean(key), null, null, null, null);
    return result;
  }

  private getNewSignObjectBean(key: NewSignObjectKeys): any {
    const configuration = store.getters.configuration as IConfiguration;
    return ObjectUtility.copyDeeply(configuration.behavior.document.editor.sign.newObjects[key].source);
  }

  // 編集中オブジェクト
  async getEditingDocumentObjects(document: Document): Promise<DocumentObjectsInformation> {
    const url = "api/document/editing-value-object";
    const documentBean = this.translator.toSaveDocumentBean(document);
    const result = await this.post(url, documentBean);
    return this.translator.toDocumentObjectsInformation(result.data);
  }

  async addDocumentOperationLog(patientCode: string, documentId: number, message: string): Promise<void> {
    const url = "api/document/" + documentId + "/add-document-operation-log";
    const data: IDocumentOperationMessageBean = {
      token: this.token,
      patientCode: patientCode,
      message: message,
    };
    await this.post(url, data);
  }

  async registerPresetCondition(condition: DocumentSearchPresetCondition): Promise<string> {
    const url = "/api/documents/preset/save";
    const query = {
      params: {
        token: this.token
      }
    };
    const conditionBean = this.translator.toDocumentSearchPresetConditionBean(condition);
    const result = await this.post(url, conditionBean, query);
    return result.data;
  }

  async getPresetConditions(departmentId: string): Promise<DocumentSearchPresetCondition[]> {
    const url = "/api/documents/preset/" + departmentId;
    const query = {
      params: {
        token: this.token,
      }
    };
    const result = await this.get(url, query);
    return this.translator.toPresetSearchConditions(result.data);
  }

  async deletePresetCondition(condition: DocumentSearchPresetCondition): Promise<void> {
    const url = "/api/documents/preset/delete";
    const query = {
      params: {
        token: this.token
      }
    };
    const conditionBean = this.translator.toDocumentSearchPresetConditionBean(condition);
    await this.post(url, conditionBean, query);
  }

  async registerPresetOrder(keys: string[]): Promise<void> {
    const url = "/api/documents/preset/order/save";
    const query = {
      params: {
        token: this.token
      }
    };
    const orderBean = this.translator.toDocumentSearchPresetConditionOrderBeans(keys);
    await this.post(url, orderBean, query);
  }

  async getPresetOrder(): Promise<string[]> {
    const url = "/api/documents/preset/order";
    const query = {
      params: {
        token: this.token,
      }
    };
    const result = await this.get(url, query);
    return this.translator.toDocumentSearchPresetConditionKeys(result.data);
  }

  async deleteDocumentWriteToken(writeToken: string): Promise<void> {
    if (!writeToken) { return; }

    const url = "/api/document/write-token/" + writeToken + "/delete";
    const query = {
      params: {
        token: this.token,
      }
    };
    await this.post(url, query);
  }

  async extendWriteTokenLifeSpan(writeToken: string): Promise<void> {
    if (!writeToken) { return; }

    const url = "/api/document/write-token/" + writeToken + "/extend";
    const query = {
      params: {
        token: this.token,
      }
    };
    await this.post(url, query);
  }

  async isOtherPatientDocumentEditing(patientCode: string): Promise<boolean> {
    const url = "/api/document/is-other-patient-document-editing";
    const query = {
      params: {
        token: this.token,
        patientCode: patientCode,
      }
    };
    const result = await this.get(url, query);
    return result.data;
  }

  async searchWizardDocuments(param: DocumentSearchParameter): Promise<DocumentSummary[]> {
    const url = "/api/wizard/documents";
    const parameters = {
      patientId: param.patientId,
      startDate: param.fromDate,
      endDate: param.toDate,
      departmentId: param.departmentId,
      containsDeleted: param.containsDeleted,
      containsTemporary: param.containsTemporary,
      containsRegistered: param.containsRegistered,
      documentKindIds: param.documentKindIds,
      loginUserId: param.loginUserId,
      contentDateSearch: param.contentDateSearch,
      loginUserOnly: param.loginUserOnly,
      formatName: param.formatName,
      formatIds: param.formatIds,
      manageNumberText: param.manageNumberText,
      containsVirtualDocuments: param.containsVirtualDocuments,
      wizardKey: "guest",
      updateUserCodes: param.updateUserCodes,
    };
    const result = await this.post(url, parameters);
    console.debug(result.data);
    return this.toDocumentSummaries(result.data);
  }

  async getDocumentPublishedHistory(documentId: number): Promise<IDocumentPublished[]> {
    const url = "/api/document/" + documentId + "/documentPublished";
    const query = {
      params: {
        token: this.token,
      }
    };
    const result = await this.get(url, query);
    return this.translator.toDocumentPublishedHistory(result.data);
  }

  async getQuotableDestinationFormatIds(quoteSourceDocumentId: number): Promise<number[]> {
    const url = "/api/document/" + quoteSourceDocumentId + "/quotable-destination-formats";
    const query = {
      params: {
        token: this.token,
      }
    };
    const result = await this.get(url, query);
    return result.data as number[];
  }

  async registerPresetSortCondition(conditions: DocumentListPresetSortCondition[]): Promise<void> {
    const url = "/api/documents/sort/preset/save";
    const query = {
      params: {
        token: this.token
      }
    };
    const conditionBeans = this.translator.toPresetSortConditionBeans(conditions);
    await this.post(url, conditionBeans, query);
  }

  async getPresetSortCondition(): Promise<DocumentListPresetSortCondition[]> {
    const url = "/api/documents/sort/preset";
    const query = {
      params: {
        token: this.token
      }
    };
    const result = await this.get(url, query);
    return this.translator.toDocumentListPresetSortConditions(result.data);
  }

  async registerCommonDocumentSortConditions(conditions: DocumentListPresetSortCondition[]): Promise<void> {
    const url = "/api/documents/sort/preset/common/save";
    const query = {
      params: {
        token: this.token
      }
    };
    const conditionBeans = this.translator.toPresetSortConditionBeans(conditions);
    await this.post(url, conditionBeans, query);
  }

  async getCommonPresetSortConditions(): Promise<DocumentListPresetSortCondition[]> {
    const url = "/api/documents/sort/preset/common";
    const query = {
      params: {
        token: this.token
      }
    };
    const result = await this.get(url, query);
    return this.translator.toDocumentListPresetSortConditions(result.data);
  }

  async getSingleEntryCheckInformation(patientCode: string, formatOriginalId: number): Promise<SingleEntryCheckInformation> {
    const url = "/api/document/single-entry-check";
    const query = {
      params: {
        token: this.token,
        patientcode: patientCode,
        formatOriginalId: formatOriginalId,
      }
    };
    const result = await this.get(url, query);
    return this.toSingleEntryCheckInformation(result.data);
  }

  private toSingleEntryCheckInformation(bean: ISingleEntryCheckInformationBean): SingleEntryCheckInformation {
    if (!bean) { return new SingleEntryCheckInformation(); }
    const info = new SingleEntryCheckInformation();
    info.registeredDocumentSummary = bean.registeredDocumentSummary ? this.toDocumentSummary(bean.registeredDocumentSummary) : null;
    info.editInformation = this.toEditInformation(bean.editInformation);
    return info;
  }

  private toEditInformation(bean: ICurrentEditorBean): CurrentEditors {
    if (!bean) { return null; }
    return new CurrentEditors(bean.userName, bean.terminalName, this.yyyyMmDdHhMmSsToDate(bean.editStartDateTime), null);
  }

  public async getUserStampImageSource(userCode: string): Promise<IDocumentImageSource> {
    const url = "/api/document/user-stamp-cache";
    const query = {
      params: {
        token: this.token,
        userCode: userCode,
      }
    };
    const result = await this.get(url, query);
    const imageGuid = result.data;
    return !!imageGuid
      ? new ImageSource(() => this.token, null, null, ImageSourceKinds.Cache, imageGuid)
      : null;
  }
}
